import { useEffect, useState, useCallback } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "bsca_pwa_install_dismissed";

// Detects if the app is already running as an installed PWA
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !window.MSStream;
};

const InstallPWA = () => {
  const { settings } = useAuth();
  const logo = settings?.logo_base64;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  const dismissed = useCallback(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (isStandalone() || dismissed()) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // iOS Safari never fires beforeinstallprompt; show manual instructions
    if (isIOS() && !isStandalone() && !dismissed()) {
      setVisible(true);
    }

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    setShowIOSHelp(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-3 pb-3 md:bottom-4 md:right-4 md:left-auto md:justify-end md:px-0 md:pb-0"
      data-testid="pwa-install-banner"
    >
      <div className="w-full max-w-md rounded-xl border border-[#d4af37]/40 bg-[#0f172a] text-white shadow-2xl">
        {!showIOSHelp ? (
          <div className="flex items-center gap-3 p-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/95 p-1">
              {logo ? (
                <img src={logo} alt="Logo" className="h-full w-full object-contain" data-testid="pwa-install-logo" />
              ) : (
                <Download className="h-5 w-5 text-[#d4af37]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Install BSCA App</p>
              <p className="text-xs text-slate-300 leading-tight">
                Add to your home screen for full-screen, faster access.
              </p>
            </div>
            <button
              onClick={handleInstall}
              className="shrink-0 rounded-lg bg-[#d4af37] px-3 py-2 text-xs font-semibold text-[#0f172a] transition-colors hover:bg-[#c19f2f]"
              data-testid="pwa-install-btn"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:text-white"
              aria-label="Dismiss install prompt"
              data-testid="pwa-dismiss-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="p-4" data-testid="pwa-ios-help">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">Install on iPhone / iPad</p>
              <button
                onClick={handleDismiss}
                className="rounded-lg p-1 text-slate-400 hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-2 text-xs text-slate-200">
              <li className="flex items-center gap-2">
                <span className="font-semibold text-[#d4af37]">1.</span>
                <span className="flex items-center gap-1">
                  Tap the <Share className="inline h-4 w-4 text-[#d4af37]" /> Share button in Safari.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="font-semibold text-[#d4af37]">2.</span>
                <span className="flex items-center gap-1">
                  Choose <PlusSquare className="inline h-4 w-4 text-[#d4af37]" /> "Add to Home Screen".
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="font-semibold text-[#d4af37]">3.</span>
                <span>Tap "Add" to finish.</span>
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPWA;
